// RSS Subscription API Service
import { API_BASE_URL } from '../config/api';

export const FREE_USER_REFRESH_LIMIT = 2;

const parseApiError = async (response, fallbackMessage) => {
    let payload = {};
    try {
        payload = await response.json();
    } catch {
        payload = {};
    }

    const error = new Error(payload.message || payload.error || fallbackMessage);
    error.status = response.status;
    error.payload = payload;
    throw error;
};

const ensureRssPermission = (error) => {
    if (error?.status === 403) {
        const nextError = new Error('当前账号无 RSS 管理权限（仅超管可访问）');
        nextError.status = 403;
        throw nextError;
    }
    throw error;
};

export const getRssFeeds = async (token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/hotspot/rss/`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        try {
            await parseApiError(response, '获取订阅列表失败');
        } catch (error) {
            ensureRssPermission(error);
        }
    }

    const data = await response.json();
    return (data || []).map((item) => ({
        ...item,
        name: item.name || item.title || '未命名订阅',
        last_updated: item.last_updated || item.last_fetched_at || null,
    }));
};

export const addRssFeed = async (feed, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/hotspot/rss/create/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            title: feed.name || feed.title || '',
            url: feed.url,
            is_active: true,
        }),
    });

    if (!response.ok) {
        try {
            await parseApiError(response, '添加订阅失败');
        } catch (error) {
            ensureRssPermission(error);
        }
    }

    const data = await response.json();
    return {
        ...data,
        name: data.name || data.title || '未命名订阅',
        last_updated: data.last_updated || data.last_fetched_at || null,
    };
};

export const deleteRssFeed = async (feedId, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/hotspot/rss/${feedId}/delete/`, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        try {
            await parseApiError(response, '删除订阅失败');
        } catch (error) {
            ensureRssPermission(error);
        }
    }

    return response.json();
};

export const refreshRssFeed = async (feedId, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/hotspot/rss/refresh/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ feed_id: feedId }),
    });

    if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        if (response.status === 403 && payload?.error !== 'permission_denied') {
            throw new Error('免费用户每日刷新次数已用完');
        }
        const error = new Error(payload.message || payload.error || '刷新失败');
        error.status = response.status;
        ensureRssPermission(error);
    }

    return response.json();
};

export const getFeedItems = async () => {
    // 当前版本不开放 RSS 文章列表接口给前端，保持空数组兼容页面渲染
    return [];
};

export const getRefreshStatus = async () => ({
    remaining: FREE_USER_REFRESH_LIMIT,
    reset_at: null,
});

export const validateRssUrl = async (url) => {
    try {
        const parsed = new URL(url);
        const valid = parsed.protocol === 'http:' || parsed.protocol === 'https:';
        return { valid, title: parsed.hostname };
    } catch {
        return { valid: false };
    }
};

export const RSS_CATEGORIES = [
    { id: 'tech', name: '科技', icon: '💻' },
    { id: 'news', name: '新闻', icon: '📰' },
    { id: 'finance', name: '财经', icon: '💰' },
    { id: 'lifestyle', name: '生活', icon: '🌿' },
    { id: 'entertainment', name: '娱乐', icon: '🎬' },
    { id: 'other', name: '其他', icon: '📌' },
];

const rssService = {
    getRssFeeds,
    addRssFeed,
    deleteRssFeed,
    refreshRssFeed,
    getFeedItems,
    getRefreshStatus,
    validateRssUrl,
    RSS_CATEGORIES,
    FREE_USER_REFRESH_LIMIT,
};

export default rssService;
